// Inline help affordance (Spec §6.3) — a small ? button beside a section header
// or the PageHeader title that opens a short popover with the answer, plus a
// "Learn more" link into the full Resources panel. Answers live where the
// question is asked.
//
// Rendered through a portal to document.body (Story #5305 Stream 14d) so the
// popover is never clipped by an ancestor's overflow:hidden/auto — e.g. the
// Overlap view's scrolling group list — the way an absolutely-positioned
// child of that container would be.

import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { HelpCircle, X } from 'lucide-react'
import { helpFor } from '../../constants/helpContent'
import { requestResourcesPanel } from './helpBus'

interface HelpPopoverProps {
  /** helpContent.ts key */
  topic: string
  /** override title/body when not using a keyed article */
  title?: string
  body?: string
  /** accessible label for the trigger */
  label?: string
  size?: number
}

const MARGIN = 8
const PANEL_WIDTH = 288

export default function HelpPopover({ topic, title, body, label, size = 16 }: HelpPopoverProps) {
  const article = helpFor(topic)
  const resolvedTitle = title ?? article?.title ?? 'Help'
  const resolvedBody = body ?? article?.body ?? ''
  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const panelId = useId()

  const reposition = () => {
    const el = triggerRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const left = Math.min(
      Math.max(rect.left + rect.width / 2 - PANEL_WIDTH / 2, MARGIN),
      window.innerWidth - PANEL_WIDTH - MARGIN,
    )
    setCoords({ top: rect.bottom + 8, left })
  }

  useLayoutEffect(() => {
    if (!open) return
    reposition()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (!open) return
    function onDoc(e: MouseEvent) {
      const target = e.target as Node
      if (triggerRef.current?.contains(target)) return
      if (panelRef.current?.contains(target)) return
      setOpen(false)
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onEsc)
    window.addEventListener('scroll', reposition, true)
    window.addEventListener('resize', reposition)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onEsc)
      window.removeEventListener('scroll', reposition, true)
      window.removeEventListener('resize', reposition)
    }
  }, [open])

  if (!resolvedBody && !resolvedTitle) return null

  const handleLearnMore = () => {
    setOpen(false)
    requestResourcesPanel(topic)
  }

  return (
    <span className="relative inline-flex">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={label ?? `Help: ${resolvedTitle}`}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        className="inline-flex items-center justify-center rounded-full text-slate-400 hover:text-iflow-600 focus:outline-none focus:ring-2 focus:ring-iflow-200"
      >
        <HelpCircle size={size} aria-hidden />
      </button>
      {open &&
        coords &&
        createPortal(
          <div
            ref={panelRef}
            id={panelId}
            role="dialog"
            aria-label={resolvedTitle}
            data-testid="help-popover-panel"
            className="fixed z-[70] rounded-xl border border-slate-200 bg-white p-3 text-left shadow-xl"
            style={{ top: coords.top, left: coords.left, width: PANEL_WIDTH }}
          >
            <div className="mb-1 flex items-start justify-between gap-2">
              <p className="text-sm font-semibold text-iflow-900">{resolvedTitle}</p>
              <button type="button" onClick={() => setOpen(false)} aria-label="Close" className="text-slate-400 hover:text-slate-600">
                <X size={14} />
              </button>
            </div>
            <p className="text-sm leading-relaxed text-slate-600">{resolvedBody}</p>
            {article && (
              <button
                type="button"
                onClick={handleLearnMore}
                data-testid="help-popover-learn-more"
                className="mt-2 text-xs font-semibold text-iflow-600 hover:text-iflow-800 hover:underline"
              >
                Learn more →
              </button>
            )}
          </div>,
          document.body,
        )}
    </span>
  )
}
